// The homepage's lower spine: the Method band, who it's for, the pricing honesty strip, the closing CTA and the consumer-health-privacy notice.
// D5 binds every word here — no clinical register (the enforced list is `BANNED` in `splash-gates.dom.test.tsx`, wider than any hand-typed one) and no promise of a clinical benefit. And nothing near narration may read as opt-in ("enable", "turn on", "opt in"): narration is unconditional, so an opt-in framing is not a softer claim, it is a false one.
// Node-prerender-safe: MUI + react-router + Setup/Mui + the two shared route modules only.
import { Link as RouterLink } from 'react-router';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MUILink from '@mui/material/Link';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import TokenOutlinedIcon from '@mui/icons-material/TokenOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import EditNoteIcon from '@mui/icons-material/EditNoteOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenterOutlined';
import CasinoOutlinedIcon from '@mui/icons-material/CasinoOutlined';
import { SectionHeader, FeatureCard, accentColor } from './splash-sections';
import { CREDIT_TOPUP_DISPLAY, CREDITS_PER_PURCHASE, formatCredits } from '../../../../shared/config/credits';

/** Mined from the retired `Splash/Home/HowItWorks.tsx`. The three paragraphs are that file's prose, compressed; the pull-quote is verbatim and is promoted to the TOP of the band instead of dying at the bottom of a wall of text. TWO of its claims were false and are not carried over: the closing "Opt a Series into podcasting" sentence (narration is unconditional), and ¶2's unqualified "the real characters, events, and themes of your life build into a Ledger" — `data/schemas/ledger.ts`'s `kind` comment and `shared/utils/ledgerEdit.ts:11-17` scope `character`/`event`/`place` to the fiction/spoilers path, so only `theme` accumulates on a nonfiction Series. Porting prose verbatim is exactly how a dead claim survives a rewrite; re-read the anchor before restoring a sentence from that file. */
const METHOD_BEATS = [
  { label: 'The practice', body: 'Self-Authorship is the practice of stepping back from the flow of your days and treating your own life as a narrative you can examine, revise, and shape. Gnothi builds that into a daily writing practice - and you can use it as a creative-writing aid, a steerable novel, even a DnD campaign tool. You take the reins.' },
  { label: 'The mechanism', body: 'Stay active and Gnothi authors Chapters - generated installments saved back as entries - that grow richer over time. Arc memory lets each Series accumulate context across sessions: the themes your life keeps returning to build into a Ledger the arc draws on, joined on a fiction Series by its characters, events and places. Choose fiction (Fantasy, Sci-Fi, Literary, and more) or an adaptive nonfiction arc that reads how you write and responds in kind.' },
  { label: 'The return', body: 'When someone shares their entries with you, they weave into your Series - lending your arc a second perspective, not just your own. And every Chapter is narrated for you as it lands, so the book of your life is also something you can listen to on the way to work.' },
];

export function HomeMethod() {
  return (
    <>
      <SectionHeader
        eyebrow="How it works"
        title="Self-Authorship: write the story of your life"
        deck="An AI journal you write in daily, that writes back. Write. Track Fields. Watch your Series unfold, Chapter by Chapter."
      />
      <Grid container spacing={{ xs: 4, md: 6 }} sx={{ alignItems: 'flex-start' }}>
        <Grid size={{ xs: 12, md: 5 }}>
          {/* An accent FILL, so it takes the `splash.accent`/`splash.onAccent` PAIR — white italic body on `primary.main` was 2.7:1, failing AA at this size, and `primary.dark` on the stripe already pointed at the darker stop. */}
          <Paper
            elevation={0}
            sx={{ p: 3.5, borderLeft: 6, borderColor: 'primary.dark', bgcolor: 'splash.accent', backgroundImage: 'none', borderRadius: 2 }}
          >
            <Typography variant="body1" sx={{ color: 'splash.onAccent', fontStyle: 'italic', fontSize: '1.0625rem', lineHeight: 1.7 }}>
              "You'll only find it the way you find the floor of any valley. One step. Then another. Toward something warm you didn't put there to be chosen by - only to sit near."
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack spacing={3} sx={{ color: 'text.secondary', '& p': { fontSize: '1.125rem' } }}>
            {METHOD_BEATS.map((b) => (
              <Box key={b.label}>
                <Typography variant="overline" sx={{ display: 'block', color: accentColor(), fontWeight: 700, letterSpacing: 1 }}>{b.label}</Typography>
                <Typography variant="body1">{b.body}</Typography>
              </Box>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </>
  );
}

const AUDIENCES = [
  { title: 'The journaler', icon: <EditNoteIcon />, tone: 'prompt' as const, body: 'Write the way you already write, and get something back for it: a Chapter that read your week and had a point of view about it. Uses Series, and Fields once you want to know what your weeks have in common.' },
  { title: 'The creative writer', icon: <MenuBookOutlinedIcon />, tone: 'organization' as const, body: 'A steerable novel with a source you never run out of. Pick the genre, seed the arc, and edit the Ledger when the story needs a different fact. Uses Series.' },
  { title: 'The habit tracker', icon: <FitnessCenterIcon />, tone: 'behavior' as const, body: 'Track anything, with scoring light enough that a broken streak stays informative. Then read the causal graph for which of your fields actually moves the others. Uses Fields, and Series to see it written up.' },
  { title: 'The DnD table', icon: <CasinoOutlinedIcon />, tone: 'themes' as const, body: 'Run the campaign as a Group: everyone writes their session entries, the arc weaves them into one telling, and anonymity is a role you can hand out. Uses Community and Series.' },
];

export function HomeAudiences() {
  return (
    <>
      <SectionHeader title="Who it's for" deck="Four ways people actually use it. Pick the one that sounds like you - the others are still there when you want them." />
      <Grid container spacing={2}>
        {AUDIENCES.map((a) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={a.title}>
            {/* `accent`: four cards, four DIFFERENT domain tones — the top rule is what makes them read as four kinds of person rather than four of a kind. */}
            <FeatureCard title={a.title} icon={a.icon} tone={a.tone} accent>{a.body}</FeatureCard>
          </Grid>
        ))}
      </Grid>
    </>
  );
}

// The three columns ARE the two-axis gate in `shared/utils/aiAccess.ts`: axis A (`hasPaidAccess` = has_paid || is_cool || is_superuser) permanently unlocks a feature; axis B (`aiActive` = credits + consent, and it never reads `has_paid`) meters every fresh AI run. The MIDDLE column was missing, and adding it is the correction — Chapters are axis B, not paid: `entries_gen_chapters_request` is gated `assertAiActive` ("chapters are METERED, not paid-tier") and `stories/autopilot.ts`'s due-set selects `is_cool OR is_superuser OR credits > 0` plus consent, with NO `has_paid`. Same axis for the causal analysis (`ml/influencers.ts`) and for every web `PremiumLock` callsite — dictation, story/voice chat, field analysis — whose own header says it mirrors axis B and not `has_paid`.
// Narration sits on NEITHER axis: `routes/tts/chapter-audio.ts` bills nothing on the `source:'ai'` path (only a user-initiated redo runs `assertTtsCredits`), and `Stories.attachDerived` mints the podcasts row for every Serial, so there is no audio-production gate to describe at all.
// PAID is the CLIENT-REACHABLE axis-A set: Groups splits on the VERB, not the feature (`#1840`) — the five `requirePayment()` call sites in `routes/groups/index.ts` are create/edit/delete a group, member-role/ban and invite, while join, post, vote, pseudonym and browse are free and NEITHER client gates the subtree (`PaymentGate` is deleted); `requirePaidTier` on the summary/sensitive share tiers at create/edit only; and `canUseCustomVoice` on clone/design voices. The previous "EXACTLY the set of axis-A gates in the tree" was FALSE — `stories/pull.ts`'s insights manifest ("Free users never see insights", plus `gates(assertPaidAccess, assertAiActive)` on the `_response`) and `entries_reframe_request` are axis-A too. Both are deliberately NOT sold here because no shipped client calls either route; re-grep `hasPaidAccess|assertPaidAccess` before re-asserting completeness.
const TIERS = [
  {
    title: 'Free, and never metered',
    icon: <CheckCircleOutlineIcon fontSize="small" />,
    items: [
      'Journaling, tags, and as many Fields as you want',
      'Chapter narration - every Chapter, always, at full voice quality',
      'A real podcast feed for your Series, public or private',
      'Sharing at the "full" privacy tier, with anyone you choose',
    ],
  },
  {
    title: 'Free, on the credits you start with',
    icon: <TokenOutlinedIcon fontSize="small" />,
    items: [
      'Series, and the Chapters they author - on demand from an entry, or on a cadence you set',
      'The causal analysis over your Fields, and the plain-language read of what it found',
      'Story chat, and dictation when you would rather talk than type',
    ],
  },
  {
    title: 'Unlocked by the one-time top-up',
    icon: <LockOpenOutlinedIcon fontSize="small" />,
    items: [
      'Starting a group of your own - joining and posting in one is free',
      'The "summary" and "sensitive" share tiers - the ones that put an AI between your entry and its reader',
      'Cloning or designing a narration voice of your own - the four preset voices are free',
    ],
  },
];

/** Dollar figures are DERIVED from src/shared/config/credits.ts, never typed: there is exactly one top-up price in this codebase and marketing copy is not allowed to be the second definition of it. */
export function HomePricing() {
  return (
    <>
      <SectionHeader
        eyebrow="No subscription, no trial clock"
        title="What's free, what runs on credits, and what isn't"
        deck={`Every account starts with a credit balance the AI lanes draw on; a one-time ${CREDIT_TOPUP_DISPLAY} top-up adds ${formatCredits(CREDITS_PER_PURCHASE)} more and unlocks the three paid capabilities for good.`}
      />
      <Grid container spacing={{ xs: 3, md: 4 }}>
        {TIERS.map((t) => (
          <Grid size={{ xs: 12, md: 4 }} key={t.title}>
            <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 2 }}>{t.title}</Typography>
            <Stack spacing={1.25}>
              {t.items.map((item) => (
                <Stack direction="row" spacing={1.25} key={item} sx={{ alignItems: 'flex-start' }}>
                  {/* These checks carry MEANING — they mark what a tier includes — so 3:1 applies and `primary.main` (2.6:1 on this grey band) does not clear it. */}
                  <Box sx={{ color: accentColor(), display: 'flex', pt: '2px' }}>{t.icon}</Box>
                  <Typography variant="body2" color="text.secondary">{item}</Typography>
                </Stack>
              ))}
            </Stack>
          </Grid>
        ))}
      </Grid>
    </>
  );
}

/** Ported VERBATIM from the retired `Splash/Home/index.tsx`, styling included — a compliance element, not copy. `home.dom.test.tsx` pins its text and its link so a later language pass cannot quietly drop it. */
export function HomeComplianceNotice() {
  return (
    <Box sx={{ px: 3, pb: 4, display: 'flex', justifyContent: 'center' }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          maxWidth: 720,
          px: 2.5, py: 1.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}>
        <HealthAndSafetyIcon color="primary" />
        <Typography variant="body2" color="text.secondary">
          Gnothi handles your mood, journal, and voice data as consumer health data. Read our{' '}
          <MUILink component={RouterLink} to="/consumer-health-privacy" underline="hover">
            Consumer Health Data Privacy Policy
          </MUILink>.
        </Typography>
      </Stack>
    </Box>
  );
}
